// A QR code with the UMaps mark in the middle.
//
// One definition of that, shared by the printed building signs, the Event Map
// Creator's share step and the public event page's QR popup — because they are
// the same artefact and a club that sees a badged code in the creator and a
// bare one on its own event page reads the difference as a bug.
//
// ⚠️ COPIED BYTE-FOR-BYTE INTO UMapsTools/src/utils/qrBadge.js, beside the copy
// of `qrcode.js` it already keeps, so the internal tool's QR Code Directory
// prints the same sign this repo does. Edit HERE and copy across; the two
// diverging is how a sheet printed from the tool and one printed from
// /buildingdirectories stop being the same object. That also means the mark
// lives at the same path in both `public/` folders.
//
// ── One geometry, set by the app's share sheet ──────────────────────────────
// The numbers below are NOT chosen here. They are read off UMapsMobile's
// "Share UMaps with friends" code (`src/share/QrCode.tsx` +
// `src/share/qrPath.ts`), which is the reference this product measures every
// other code against: a white disc at `LOGO_FRACTION` of the code's drawn
// extent, the mark at 0.76 of that disc, and a thin accent ring round the disc.
// A sign on a wall, a poster a club downloads and the code in the app are one
// artefact, and the only way they stay one is by sharing the arithmetic rather
// than the intent.
//
// ── Why a hole in a QR is safe ──────────────────────────────────────────────
// `qrcode.js` encodes at error-correction level M, which recovers ~15% of the
// symbol — the SAME level and the same reasoning as the reference. The disc is
// `LOGO_FRACTION` of the drawn extent, so on a version-5 code (n=37, extent 45)
// it covers π(0.105 × 45)² ≈ 70 modules of 1369, i.e. **~5% of the symbol** —
// a third of the budget, leaving the rest for fingerprints, glare and the bad
// angle a phone camera actually arrives at. ⚠️ If these fractions ever grow,
// check that arithmetic again: past ~15% the code stops scanning, and it does
// so gradually and per-device, which is the worst way for a printed poster to
// fail. `src/qrbadge.test.js` asserts the budget for exactly that reason.
//
// ── Two renderings, because there are two destinations ──────────────────────
//  · `qrBadgeSvg` draws the code, the white disc and the ring, and expects the
//    mark to be laid over it as a DOM `<img>`. That is for ON SCREEN only,
//    where a missing image is a refresh away from being fixed.
//  · `qrBadgeSvgEmbedded` puts the mark INSIDE the SVG as a data URI. Anything
//    that LEAVES the page uses this — a download and a print sheet both. A
//    downloaded file has to stand on its own (a club opens it in Illustrator or
//    hands it to a print shop, where a `/UMaps%20Logo…` reference resolves to
//    nothing and the middle prints empty), and a print sheet has to survive a
//    `window.print()` that fires before an overlaid `<img>` has decoded —
//    which prints a hundred cards with a hole in the middle and no error
//    anywhere.
//
// ⚠️ The white disc and the accent ring are drawn INSIDE the SVG in both, never
// as a CSS background: browsers drop background colours when "background
// graphics" is off, which is the default in most print dialogs. A ring that
// vanishes on the one sheet that gets laminated is exactly the bug worth
// avoiding.

import { qrSvg } from './qrcode.js';

/**
 * The white disc's diameter, as a fraction of the code's DRAWN EXTENT.
 *
 * ⚠️ Extent, not symbol: the quiet zone is inside the measurement, because that
 * is how the reference measures it (`size` in `QrCode.tsx` is the whole drawn
 * square) and a fraction of two different denominators is two different discs.
 *
 * 0.21 is `LOGO_FRACTION` in UMapsMobile/src/share/qrPath.ts, where the number
 * is argued: it is bounded by the error-correction budget, not by taste. Keep
 * the two equal — a code on a poster and the same code in the app differing by
 * a few per cent is the kind of thing nobody reports and everybody notices.
 */
export const LOGO_FRACTION = 0.21;

/**
 * The mark's width as a fraction of the disc it sits on.
 *
 * The remainder is not padding: it is the mark's own quiet zone. A logo dropped
 * straight onto modules blurs into them and reads as a smudge rather than a
 * badge, which is the whole reason the disc exists.
 */
export const MARK_OF_DISC = 0.76;

/**
 * How wide the MARK is, as a fraction of the drawn extent.
 *
 * Kept under its old name because every caller uses it for exactly that — the
 * CSS width of the `<img>` laid over the code — but it is now derived rather
 * than typed, so the disc and the mark cannot drift apart.
 */
export const BADGE = LOGO_FRACTION * MARK_OF_DISC;

/**
 * That same width as a CSS percentage, so a stylesheet never restates it.
 *
 * Three copies of "22%" in three page stylesheets is how the printed sign and
 * the event poster stopped matching the app in the first place.
 */
export const MARK_CSS_WIDTH = `${(BADGE * 100).toFixed(2)}%`;

/**
 * The accent ring round the disc: its width as a fraction of the disc.
 *
 * The reference draws a 2px border on a 55px plate (the share sheet's 264pt
 * code at `LOGO_FRACTION`), so the ring is 2/55 of the disc. Expressed as a
 * fraction rather than in pixels because every caller here scales the SVG with
 * CSS — `qrBadgeSvg(url, 420)` routinely renders at 260px — so a pixel width
 * would be a different ring on every screen it appears on.
 */
export const RING_OF_DISC = 0.036;

/**
 * The ring's colour: the app's default accent, matching `colors.accent`.
 *
 * Fixed rather than themed on purpose. A per-page accent (`--accent` is indigo
 * on the events pages, UM blue on a sign) would make the mark a different
 * object on each screen, and the mark is the one thing here that is supposed to
 * be identical everywhere.
 */
export const RING_COLOR = '#1a73e8';

/**
 * The mark itself.
 *
 * Percent-encoded because the filename has spaces in it, and an unencoded space
 * in a src is the trap that has bitten this repo before. This replaced
 * `/umaps-logo.png`, which did not exist: the host answered it with index.html
 * and a 200, so the request looked fine and the centre of every card printed as
 * a broken image.
 */
export const UMAPS_BADGE_URL = '/UMaps%20Logo%20Circle%20Border.png';

/**
 * The code's drawn extent, in module units — the symbol plus its quiet zone.
 *
 * ⚠️ `qrSvg` emits a viewBox in MODULE units (e.g. "0 0 45 45"), not pixels —
 * the width/height attributes carry the pixel size. Anything placed at pixel
 * coordinates lands hundreds of units off-canvas and renders as nothing at all,
 * silently. The count varies with the payload's version, so it is read back off
 * the viewBox rather than assumed.
 */
const extentOf = (svg) => Number(/viewBox="0 0 (\d+)/.exec(svg)?.[1]) || 0;

/** The white disc the mark sits on, radius in module units. */
const discRadius = (n) => (n * LOGO_FRACTION) / 2;

/**
 * The code, the white disc and the accent ring. The mark goes over it as a DOM
 * `<img>` sized at `MARK_CSS_WIDTH`.
 *
 * The ring is stroked INSIDE the disc rather than on its edge, so the disc's
 * outer boundary is `discRadius` either way. That matches the reference, where
 * the ring is a React Native `borderWidth` and RN borders are drawn inward — a
 * centred stroke would push accent colour out over the modules and eat into the
 * mark's quiet zone from the wrong side.
 */
export function qrBadgeSvg(text, size) {
  const svg = qrSvg(text, { size });
  const n = extentOf(svg);
  if (!n) return svg;
  const c = n / 2;
  const r = discRadius(n);
  const ring = r * 2 * RING_OF_DISC;
  return svg.replace('</svg>',
    `<circle cx="${c}" cy="${c}" r="${r}" fill="#fff"/>`
    + `<circle cx="${c}" cy="${c}" r="${r - ring / 2}" fill="none"`
    + ` stroke="${RING_COLOR}" stroke-width="${ring}"/></svg>`);
}

/**
 * The code, the disc and the mark, in one self-contained file.
 *
 * `logoDataUrl` is a data: URI from `loadBadgeDataUrl()`. Given anything else —
 * including null, which is what that function returns when the image could not
 * be read — this falls back to `qrBadgeSvg`, so the worst case is a code with a
 * white centre rather than one with a broken image in it.
 */
export function qrBadgeSvgEmbedded(text, size, logoDataUrl) {
  const svg = qrBadgeSvg(text, size);
  const n = extentOf(svg);
  if (!n || typeof logoDataUrl !== 'string' || !logoDataUrl.startsWith('data:')) return svg;
  const w = n * BADGE;
  const at = (n - w) / 2;
  // The mark is square (900×900), so no aspect fitting is needed; it is stated
  // anyway so a future non-square file cannot silently stretch into an oval.
  const image = `<image href="${logoDataUrl}" x="${at}" y="${at}" width="${w}" height="${w}"`
    + ' preserveAspectRatio="xMidYMid meet"/>';
  return svg.replace('</svg>', `${image}</svg>`);
}

// ── Reading the mark as a data URI ──────────────────────────────────────────

let pending = null;

/**
 * The badge PNG, downscaled and base64'd, for embedding in a downloaded SVG.
 *
 * ⚠️ Downscaled on purpose. The file in `public/` is 900×900 and about 300 KB,
 * which base64s to roughly 400 KB — on every QR a club downloads, for a mark
 * that prints at a fifth of the code's width. 512px is still more than a poster
 * needs and costs a fraction of that.
 *
 * Resolves to `null` rather than rejecting if anything goes wrong, because
 * every caller's right answer is the same: draw the code without the mark. A
 * download that fails outright would be a worse trade than a plain QR.
 *
 * The result is cached for the session; a FAILURE is not, so a request that
 * lost a race with a flaky network can be retried by the next download.
 */
export function loadBadgeDataUrl(px = 512) {
  if (pending) return pending;
  pending = new Promise((resolve) => {
    if (typeof document === 'undefined' || typeof Image === 'undefined') return resolve(null);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = px;
        canvas.height = px;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, px, px);
        // Same-origin, so the canvas is not tainted and toDataURL is allowed.
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = UMAPS_BADGE_URL;
  }).then((v) => {
    if (v == null) pending = null;   // don't cache a failure
    return v;
  });
  return pending;
}

/**
 * Build the SVG for a download, fetching the mark first.
 *
 * The one call a download handler needs: it never rejects, and the worst it
 * returns is a perfectly good QR with a white centre.
 */
export async function qrBadgeSvgForDownload(text, size = 1000) {
  const logo = await loadBadgeDataUrl();
  return qrBadgeSvgEmbedded(text, size, logo);
}
